import React, { useCallback } from 'react';
import { HEADING_LEVELS } from '../panels/TextMask.consts';
import { TextMaskTextSectionProps } from './TextMaskDesignPanel.types';
import {
  Text,
  TextLabel,
  FontFamilyPicker,
  Composites,
  ToggleSwitch,
  NumericInput,
  Tooltip,
  Slider,
  DropDownOption,
  DropDownThemeOption,
  DropDown,
  Divider,
  InfoIcon,
} from '@wix/wix-base-ui';
import {
  TextAlignLeft,
  TextAlignCenter,
  TextAlignRight,
  TextRtl,
  TextLtr,
  Bold,
  Italic,
  Strikethrough,
  Underline,
} from '@wix/wix-ui-icons-common/classic-editor';
import { ThumbsSelectList } from './Thumbs/ThumbsSelectList';
import { ThumbBasic as Thumbnail } from './Thumbs/ThumbBasic';
import NoTransform from '../icons/NoTransform';
import Lowercase from '../icons/Lowercase';
import Capitalize from '../icons/Capitalize';
import Uppercase from '../icons/Uppercase';
import Languages from '../icons/Languages';
import styles from '../panels/editor/textMaskPanel.editor.scss';
import trnKeys from '../panels/editor/TextMask.classic.translations.json';
import { debounce } from '@wix/wow-utils';

type HeadingLevel = keyof typeof HEADING_LEVELS;

const getMissingFontNameFallback = () => {
  return 'Arial';
};

const isThemeFont = (value: string) => /^font_\d+$/.test(value);

export const TextSection: React.FC<TextMaskTextSectionProps> = ({
  fonts,
  fontThemes,
  allowThemeControl,
  family,
  openLanguagesPanel,
  openUploadFonts,
  getMissingFontName = getMissingFontNameFallback,
  autoScale,
  size,
  allowAutoScaleControl,
  weight,
  style,
  decoration,
  align,
  direction,
  transform,
  letterSpacing,
  lineHeight,
  headingLevel,
  isDecorative,
  updateDesignWithBI,
  updateData,
  translate,
  textLabelType = 'T02',
}) => {
  const footerLinks = [
    {
      label: translate(trnKeys.section_text_fontLanguages),
      onClick: openLanguagesPanel,
      className: 'add-lang-link',
      symbol: <Languages width={16} height={16} />,
    },
    {
      label: translate(trnKeys.section_text_fontUpload),
      onClick: openUploadFonts,
      className: 'upload-font-link',
    },
  ];

  const fontsThemesDropDownOptions = () => {
    const themeOptionsProps = [];
    Object.entries(fontThemes).forEach(([key, theme]) => {
      themeOptionsProps.push({
        label: theme.themeLabel,
        value: key,
        maxFontSize: 16,
        theme: {
          fontFamily: theme.fontFamily,
        },
      });
    });

    themeOptionsProps.push({
      label: translate(trnKeys.section_text_theme_custom),
      value: 'custom',
      maxFontSize: 16,
      theme: {
        fontFamily: !isThemeFont(family)
          ? family
          : getMissingFontNameFallback(),
      },
    });

    const themeOptions = themeOptionsProps.map((themeOption) => (
      <DropDownThemeOption {...themeOption} />
    ));

    return themeOptions;
  };

  const handleThemeChange = useCallback(
    (newTheme: string) => {
      updateDesignWithBI(
        {
          family: newTheme,
        },
        {
          fieldNames: 'theme',
          fieldValues: newTheme,
        },
      );
    },
    [updateDesignWithBI],
  );

  const handleFontFamilyChange = useCallback(
    (newValue: string) =>
      updateDesignWithBI(
        { family: newValue },
        { fieldNames: 'font_family', fieldValues: newValue },
      ),
    [updateDesignWithBI],
  );

  const handleAutoScaleChange = useCallback(
    (newValue: boolean) =>
      updateDesignWithBI(
        { autoScale: newValue },
        { fieldNames: 'text_auto_scale', fieldValues: `${newValue}` },
      ),
    [updateDesignWithBI],
  );

  const handleFontSizeChange = useCallback(
    (newValue: number) =>
      updateDesignWithBI(
        { size: newValue },
        { fieldNames: 'text_size', fieldValues: `${newValue}` },
      ),
    [updateDesignWithBI],
  );

  const handleFontStyleChange = useCallback(
    (newValue: string[]) => {
      updateDesignWithBI(
        {
          weight: newValue.includes('bold') ? 'bold' : 'normal',
          style: newValue.includes('italic') ? 'italic' : 'normal',
        },
        { fieldNames: 'font_style', fieldValues: newValue.join(' ') },
      );
    },
    [updateDesignWithBI],
  );

  const handleFontDecorationChange = useCallback(
    ([newValue]: string[]) =>
      updateDesignWithBI(
        {
          decoration: newValue || 'none',
        },
        { fieldNames: 'text_decoration', fieldValues: newValue },
      ),
    [updateDesignWithBI],
  );

  const handleTextAlignChange = useCallback(
    ([newValue]: string[]) =>
      updateDesignWithBI(
        {
          align: newValue,
        },
        { fieldNames: 'text_align', fieldValues: newValue },
      ),
    [updateDesignWithBI],
  );

  const handleChangeDirection = useCallback(
    () =>
      updateData({
        direction: direction === 'rtl' ? 'ltr' : 'rtl',
      }),
    [direction, updateData],
  );

  const handleTextCapitalizeChange = useCallback(
    (newValue: string[]) =>
      updateDesignWithBI(
        {
          transform: newValue[0] as TextMaskTextSectionProps['transform'],
        },
        { fieldNames: 'text_transform', fieldValues: newValue[0] },
      ),
    [updateDesignWithBI],
  );

  const debouncedHandleLetterSpacingChange = debounce((newValue: number) => {
    if (newValue !== letterSpacing.value) {
      updateDesignWithBI(
        { letterSpacing: { value: newValue, type: 'em' } },
        { fieldNames: 'letter_spacing', fieldValues: `${newValue}` },
      );
    }
  }, 60);

  const debouncedHandleLineHeightChange = debounce((newValue: number) => {
    if (newValue !== lineHeight) {
      updateDesignWithBI(
        { lineHeight: newValue },
        { fieldNames: 'line_height', fieldValues: `${newValue}` },
      );
    }
  }, 60);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleLetterSpacingChange = useCallback(
    debouncedHandleLetterSpacingChange,
    [letterSpacing.value],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleLineHeightChange = useCallback(debouncedHandleLineHeightChange, [
    lineHeight,
  ]);

  const FontThemeSelect = () => (
    <div className={`${styles.attrContainer} ${styles.fontSelect}`}>
      <div className={styles.fontThemeLabel}>
        <TextLabel
          type={textLabelType}
          value={translate(trnKeys.section_text_theme_label)}
          shouldTranslate={false}
        />
        <InfoIcon
          text={translate(trnKeys.section_text_theme_tooltip)}
          shouldTranslate={false}
        />
      </div>
      <DropDown
        value={isThemeFont(family) ? family : 'custom'}
        shouldTranslate={false}
        onChange={handleThemeChange}
      >
        {fontsThemesDropDownOptions()}
      </DropDown>
    </div>
  );

  const FontSelect = () => (
    <div className={`${styles.attrContainer} ${styles.fontSelect}`}>
      <TextLabel
        type={textLabelType}
        value={translate(trnKeys.section_text_font)}
        shouldTranslate={false}
      />
      <FontFamilyPicker
        fonts={fonts}
        value={isThemeFont(family) ? fontThemes[family].fontFamily : family}
        getMissingFontName={getMissingFontName}
        onChange={handleFontFamilyChange}
        shouldTranslate={false}
        footerLinks={footerLinks}
      />
    </div>
  );

  const FontScaleControl = () =>
    allowAutoScaleControl && (
      <>
        <Composites.ToggleSwitch>
          <ToggleSwitch
            value={autoScale}
            labelType={textLabelType}
            label={translate(trnKeys.section_text_autoScale)}
            onChange={handleAutoScaleChange}
          />
        </Composites.ToggleSwitch>

        {!autoScale && (
          <Composites.NumericInput className={styles.textMaskFontSizeControl}>
            <TextLabel
              type={textLabelType}
              value={translate(trnKeys.section_text_fontSize)}
              shouldTranslate={false}
            />
            <NumericInput
              min={24}
              max={900}
              inputHeight="small"
              value={size}
              stepControls={true}
              onChange={handleFontSizeChange}
            />
          </Composites.NumericInput>
        )}
      </>
    );

  const TextDecorationSelect = () => (
    <div className={styles.attrContainer}>
      <TextLabel
        type={textLabelType}
        value={translate(trnKeys.section_text_styles)}
        shouldTranslate={false}
      />
      <div className={styles.decorationSelect}>
        <ThumbsSelectList
          keyId="styles"
          value={[weight, style].filter((v) => v !== 'normal')}
          noselect={true}
          multiselect={true}
          onChange={handleFontStyleChange}
          options={[
            {
              value: 'bold',
              illustration: (
                <Tooltip
                  content={translate(trnKeys.section_text_bold)}
                  closeOnMouseClick={true}
                  isHint={true}
                  shouldTranslate={false}
                >
                  <Bold />
                </Tooltip>
              ),
            },
            {
              value: 'italic',
              illustration: (
                <Tooltip
                  content={translate(trnKeys.section_text_italic)}
                  closeOnMouseClick={true}
                  isHint={true}
                  shouldTranslate={false}
                >
                  <Italic />
                </Tooltip>
              ),
            },
          ]}
        />

        <ThumbsSelectList
          keyId="decoration"
          value={[decoration]}
          noselect={true}
          onChange={handleFontDecorationChange}
          options={[
            {
              value: 'underline',
              illustration: (
                <Tooltip
                  content={translate(trnKeys.section_text_underline)}
                  closeOnMouseClick={true}
                  isHint={true}
                  shouldTranslate={false}
                >
                  <Underline />
                </Tooltip>
              ),
            },
            {
              value: 'line-through',
              illustration: (
                <Tooltip
                  content={translate(trnKeys.section_text_strikethrough)}
                  closeOnMouseClick={true}
                  isHint={true}
                  shouldTranslate={false}
                >
                  <Strikethrough />
                </Tooltip>
              ),
            },
          ]}
        />
      </div>
    </div>
  );

  const TextAlignSelect = () => (
    <div className={styles.attrContainer}>
      <TextLabel
        type={textLabelType}
        value={translate(trnKeys.section_text_alignment)}
        shouldTranslate={false}
      />
      <div className={styles.alignSelect}>
        <ThumbsSelectList
          keyId="align"
          value={[align]}
          onChange={handleTextAlignChange}
          options={[
            {
              value: direction === 'rtl' ? 'end' : 'start',
              illustration: (
                <Tooltip
                  content={translate(trnKeys.section_text_align_left)}
                  closeOnMouseClick={true}
                  isHint={true}
                  shouldTranslate={false}
                >
                  <TextAlignLeft />
                </Tooltip>
              ),
            },
            {
              value: 'middle',
              illustration: (
                <Tooltip
                  content={translate(trnKeys.section_text_align_center)}
                  closeOnMouseClick={true}
                  isHint={true}
                  shouldTranslate={false}
                >
                  <TextAlignCenter />
                </Tooltip>
              ),
            },
            {
              value: direction === 'rtl' ? 'start' : 'end',
              illustration: (
                <Tooltip
                  content={translate(trnKeys.section_text_align_right)}
                  closeOnMouseClick={true}
                  isHint={true}
                  shouldTranslate={false}
                >
                  <TextAlignRight />
                </Tooltip>
              ),
            },
          ]}
        />
        <Thumbnail
          className={styles.directionButton}
          onClick={handleChangeDirection}
          illustration={
            <Tooltip
              content={translate(trnKeys.section_text_change_text_direction)}
              shouldTranslate={false}
              closeOnMouseClick={true}
              isHint={true}
            >
              {direction === 'rtl' ? <TextRtl /> : <TextLtr />}
            </Tooltip>
          }
        />
      </div>
    </div>
  );

  const TextCapitalizeSelect = () => (
    <div className={styles.attrContainer}>
      <TextLabel
        type={textLabelType}
        value={translate(trnKeys.section_text_capitalization)}
        shouldTranslate={false}
      />
      <div className={styles.textSectionControlsCapitalization}>
        <ThumbsSelectList
          keyId="transform"
          value={[transform]}
          onChange={handleTextCapitalizeChange}
          options={[
            {
              value: 'none',
              illustration: (
                <Tooltip
                  content={translate(trnKeys.section_text_case_as_typed)}
                  closeOnMouseClick={true}
                  shouldTranslate={false}
                >
                  <NoTransform />
                </Tooltip>
              ),
            },
            {
              value: 'uppercase',
              illustration: (
                <Tooltip
                  content={translate(trnKeys.section_text_uppercase)}
                  closeOnMouseClick={true}
                  shouldTranslate={false}
                >
                  <Uppercase />
                </Tooltip>
              ),
            },
            {
              value: 'capitalize',
              illustration: (
                <Tooltip
                  content={translate(trnKeys.section_text_titlecase)}
                  closeOnMouseClick={true}
                  shouldTranslate={false}
                >
                  <Capitalize />
                </Tooltip>
              ),
            },
            {
              value: 'lowercase',
              illustration: (
                <Tooltip
                  content={translate(trnKeys.section_text_lowercase)}
                  closeOnMouseClick={true}
                  shouldTranslate={false}
                >
                  <Lowercase />
                </Tooltip>
              ),
            },
          ]}
        />
      </div>
    </div>
  );

  const CharacterSpacing = () => (
    <div className={styles.attrContainer}>
      <TextLabel
        value={translate(trnKeys.section_text_letter_spacing)}
        type={textLabelType}
        shouldTranslate={false}
      />
      <Slider
        value={letterSpacing.value}
        onChange={handleLetterSpacingChange}
        min={-1}
        max={2}
        step={0.01}
        unit="em"
      />
    </div>
  );

  const LineSpacing = () => (
    <div className={styles.attrContainer}>
      <TextLabel
        value={translate(trnKeys.section_text_line_spacing_title)}
        type={textLabelType}
        shouldTranslate={false}
      />

      <Slider
        value={lineHeight}
        placeholder=""
        min={0.1}
        max={4}
        step={0.01}
        onChange={handleLineHeightChange}
        unit="em"
      />
    </div>
  );

  const HeadingTagSelect = () => (
    <div className={styles.attrContainer}>
      <TextLabel
        value={translate(trnKeys.section_text_heading_tag)}
        type={textLabelType}
        shouldTranslate={false}
      />
      <DropDown
        className={styles.attrContainer}
        value={isDecorative ? -1 : headingLevel ?? 0}
        shouldTranslate={false}
        onChange={(newValue: number) =>
          updateData({
            isDecorative: newValue === -1,
            headingLevel: newValue > -1 ? newValue : null,
          })
        }
      >
        {Object.keys(HEADING_LEVELS).map((heading: string, index: number) => (
          <DropDownOption
            value={HEADING_LEVELS[heading as HeadingLevel]}
            label={heading}
            key={`heading${index}`}
            shouldTranslate={false}
          />
        ))}
      </DropDown>
    </div>
  );
  return (
    <div className={styles.textTab}>
      <Text weight="bold" shouldTranslate={false}>
        {translate(trnKeys.section_text)}
      </Text>
      {allowThemeControl && (
        <>
          {FontThemeSelect()}
          <Divider long />
        </>
      )}
      {FontSelect()}
      {FontScaleControl()}
      <Divider long />
      {TextDecorationSelect()}
      <Divider long />
      {TextAlignSelect()}
      <Divider long />
      {TextCapitalizeSelect()}
      <Divider long />
      {CharacterSpacing()}
      <Divider long />
      {LineSpacing()}
      <Divider long />
      {HeadingTagSelect()}
    </div>
  );
};
